import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useReportTemplates } from "@/hooks/use-report-templates";
import { REPORT_CONFIGS } from "@/pages/fleet/reports/utils/report-config";
import { LOCKED_SECTIONS, ReportTemplateSection } from "@/types/fleet-reports";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FileText,
  Save,
  RotateCcw,
  Lock,
  GripVertical,
  Image,
  AlignLeft,
  AlignCenter,
  Eye
} from "lucide-react";

export default function ReportEditor() {
  const { activeCompany } = useAuth();
  const [selectedReportType, setSelectedReportType] = useState<string>('consolidated');
  
  const { 
    templateConfig, 
    isLoading, 
    saveTemplate, 
    isSaving, 
    resetToDefault,
    defaultConfig
  } = useReportTemplates(activeCompany?.id, selectedReportType);

  const [localConfig, setLocalConfig] = useState(templateConfig);

  useEffect(() => {
    setLocalConfig(templateConfig);
  }, [templateConfig]);

  const handleSave = () => {
    saveTemplate(localConfig);
  };

  const handleReset = () => {
    setLocalConfig(defaultConfig);
    resetToDefault();
  };

  const updateSection = (sectionId: string, updates: Partial<ReportTemplateSection>) => {
    setLocalConfig(prev => ({
      ...prev,
      sections: prev.sections.map(s => 
        s.id === sectionId ? { ...s, ...updates } : s
      )
    }));
  };

  const moveSection = (sectionId: string, direction: 'up' | 'down') => {
    const sections = [...localConfig.sections].sort((a, b) => a.order - b.order);
    const index = sections.findIndex(s => s.id === sectionId);
    
    if (direction === 'up' && index > 0) {
      const temp = sections[index].order;
      sections[index].order = sections[index - 1].order;
      sections[index - 1].order = temp;
    } else if (direction === 'down' && index < sections.length - 1) {
      const temp = sections[index].order;
      sections[index].order = sections[index + 1].order;
      sections[index + 1].order = temp;
    }

    setLocalConfig(prev => ({ ...prev, sections }));
  };

  const reportTypes = REPORT_CONFIGS.filter(r => 
    ['consolidated', 'fleet-renewal', 'vehicles', 'drivers', 'maintenance', 'fuel'].includes(r.id)
  );

  const getSectionLabel = (id: string) => {
    const labels: Record<string, string> = {
      kpis: 'Indicadores (KPIs)',
      byVehicle: 'Análise por Veículo',
      byDriver: 'Análise por Motorista',
      charts: 'Gráficos',
      alerts: 'Alertas e Insights',
      items: 'Itens Detalhados'
    };
    return labels[id] || id;
  };

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-12 w-64" />
        <div className="grid grid-cols-2 gap-6">
          <Skeleton className="h-96" />
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <FileText className="h-6 w-6 text-primary" />
            Editor de Relatórios
          </h1>
          <p className="text-muted-foreground mt-1">
            Personalize a aparência e estrutura dos relatórios PDF
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleReset} disabled={isSaving}>
            <RotateCcw className="h-4 w-4 mr-2" />
            Restaurar Padrão
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? 'Salvando...' : 'Salvar Template'}
          </Button>
        </div>
      </div>

      {/* Report Type Selector */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Tipo de Relatório</CardTitle>
          <CardDescription>Selecione qual relatório deseja personalizar</CardDescription>
        </CardHeader>
        <CardContent>
          <Select value={selectedReportType} onValueChange={setSelectedReportType}>
            <SelectTrigger className="w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {reportTypes.map(report => (
                <SelectItem key={report.id} value={report.id}>
                  {report.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Configurações */}
        <div className="space-y-6">
          {/* Logo e Header */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Image className="h-4 w-4" />
                Cabeçalho do Relatório
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="logo-enabled">Exibir logo da empresa</Label>
                <Switch
                  id="logo-enabled"
                  checked={localConfig.logo.enabled}
                  onCheckedChange={(checked) => 
                    setLocalConfig(prev => ({
                      ...prev,
                      logo: { ...prev.logo, enabled: checked }
                    }))
                  }
                />
              </div>

              {localConfig.logo.enabled && (
                <div className="space-y-2">
                  <Label>Posição do logo</Label>
                  <div className="flex gap-2">
                    <Button
                      variant={localConfig.logo.position === 'left' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => 
                        setLocalConfig(prev => ({
                          ...prev,
                          logo: { ...prev.logo, position: 'left' }
                        }))
                      }
                    >
                      <AlignLeft className="h-4 w-4 mr-1" />
                      Esquerda
                    </Button>
                    <Button
                      variant={localConfig.logo.position === 'center' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => 
                        setLocalConfig(prev => ({
                          ...prev,
                          logo: { ...prev.logo, position: 'center' }
                        }))
                      }
                    >
                      <AlignCenter className="h-4 w-4 mr-1" />
                      Centro
                    </Button>
                  </div>
                </div>
              )}

              <Separator />

              <div className="space-y-2">
                <Label htmlFor="header-title">Título personalizado</Label>
                <Input
                  id="header-title"
                  placeholder="Ex: Relatório Gerencial de Frota"
                  value={localConfig.header.title}
                  onChange={(e) => 
                    setLocalConfig(prev => ({
                      ...prev,
                      header: { ...prev.header, title: e.target.value }
                    }))
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="header-subtitle">Subtítulo</Label>
                <Input
                  id="header-subtitle"
                  placeholder="Ex: Análise Mensal de Indicadores"
                  value={localConfig.header.subtitle}
                  onChange={(e) => 
                    setLocalConfig(prev => ({
                      ...prev,
                      header: { ...prev.header, subtitle: e.target.value }
                    }))
                  }
                />
              </div>
            </CardContent>
          </Card>

          {/* Seções */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Seções do Relatório</CardTitle>
              <CardDescription>
                Ative, desative e reordene as seções. 
                <span className="text-primary font-medium"> Seções marcadas como obrigatórias não podem ser desativadas.</span>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {[...localConfig.sections]
                .sort((a, b) => a.order - b.order)
                .map((section, index) => {
                  const isLocked = LOCKED_SECTIONS.includes(section.id as typeof LOCKED_SECTIONS[number]);
                  
                  return (
                    <div
                      key={section.id}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        section.enabled ? 'bg-muted/30' : 'bg-muted/10 opacity-60'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab" />
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">
                              {getSectionLabel(section.id)}
                            </span>
                            {isLocked && (
                              <Badge variant="secondary" className="text-xs flex items-center gap-1">
                                <Lock className="h-3 w-3" />
                                Obrigatório
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => moveSection(section.id, 'up')}
                          disabled={index === 0}
                        >
                          ↑
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => moveSection(section.id, 'down')}
                          disabled={index === localConfig.sections.length - 1}
                        >
                          ↓
                        </Button>
                        <Switch
                          checked={section.enabled}
                          onCheckedChange={(checked) => updateSection(section.id, { enabled: checked })}
                          disabled={isLocked}
                        />
                      </div>
                    </div>
                  );
                })}
            </CardContent>
          </Card>

          {/* Footer e Orientação */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Rodapé e Layout</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="show-pagination">Exibir paginação</Label>
                <Switch
                  id="show-pagination"
                  checked={localConfig.footer.showPagination}
                  onCheckedChange={(checked) => 
                    setLocalConfig(prev => ({
                      ...prev,
                      footer: { ...prev.footer, showPagination: checked }
                    }))
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="footer-notes">Observações do rodapé</Label>
                <Input
                  id="footer-notes"
                  placeholder="Ex: Documento gerado automaticamente"
                  value={localConfig.footer.notes}
                  onChange={(e) => 
                    setLocalConfig(prev => ({
                      ...prev,
                      footer: { ...prev.footer, notes: e.target.value }
                    }))
                  }
                />
              </div>

              <Separator />

              <div className="space-y-2">
                <Label>Orientação da página</Label>
                <div className="flex gap-2">
                  <Button
                    variant={localConfig.orientation === 'portrait' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => 
                      setLocalConfig(prev => ({ ...prev, orientation: 'portrait' }))
                    }
                  >
                    Retrato
                  </Button>
                  <Button
                    variant={localConfig.orientation === 'landscape' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => 
                      setLocalConfig(prev => ({ ...prev, orientation: 'landscape' }))
                    }
                  >
                    Paisagem
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Preview */}
        <Card className="h-fit sticky top-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Prévia do Layout
            </CardTitle>
            <CardDescription>
              Visualização simplificada da estrutura do relatório
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className={`border rounded-lg bg-white p-4 ${
              localConfig.orientation === 'landscape' ? 'aspect-[1.414/1]' : 'aspect-[1/1.414]'
            }`}>
              {/* Preview Header */}
              <div className="border-b pb-2 mb-3">
                {localConfig.logo.enabled && (
                  <div className={`h-6 w-12 bg-primary/20 rounded mb-2 ${
                    localConfig.logo.position === 'center' ? 'mx-auto' : ''
                  }`} />
                )}
                <div className={`space-y-1 ${
                  localConfig.logo.position === 'center' ? 'text-center' : ''
                }`}>
                  <div className="h-3 bg-foreground/20 rounded w-40" />
                  {localConfig.header.subtitle && (
                    <div className="h-2 bg-foreground/10 rounded w-32" />
                  )}
                </div>
              </div>

              {/* Preview Sections */}
              <div className="space-y-2">
                {[...localConfig.sections]
                  .filter(s => s.enabled)
                  .sort((a, b) => a.order - b.order)
                  .map(section => (
                    <div key={section.id} className="space-y-1">
                      <div className="h-2 bg-primary/30 rounded w-24" />
                      <div className="h-4 bg-muted rounded" />
                      {section.id === 'byVehicle' || section.id === 'byDriver' ? (
                        <div className="grid grid-cols-4 gap-1">
                          {[1, 2, 3, 4].map(i => (
                            <div key={i} className="h-2 bg-muted/60 rounded" />
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ))}
              </div>

              {/* Preview Footer */}
              {localConfig.footer.showPagination && (
                <div className="absolute bottom-4 left-0 right-0 px-4">
                  <div className="h-2 bg-foreground/10 rounded w-20 mx-auto" />
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
